import { findMoveByIdAnyScope, findMoveByIdGlobal } from '@/data/moves';
import type { Character, MoveRef } from '@/schemas/character';
import type { LevelUpGrantedChoiceDraft, LevelUpMulticlassPickDraft } from './types';

/**
 * **D7-bundle (FI-17 follow-up)** — Helper condiviso che aggrega le
 * `MoveRef` "appena acquisite" in un level-up. Usato da:
 *  - `LevelUpFlowPage.tsx::tryApplyLevelUp` per calcolare le
 *    spell-grants attive da passare a `applyLevelUp`.
 *  - `LevelUpStepSpells.tsx` per la UI delle grant attive nello step.
 *
 * Include:
 *  - `moveAdded` (mossa main scelta dal giocatore).
 *  - mossa concessa via `grantedChoice` `from-group` (FI-31 / FI-35 Gap D).
 *  - mossa-target multiclasse (FI-17 / D7).
 *  - mosse del bundle (D7-bundle): sia di main (es. *La Canzone delle
 *    Fate*) sia di multiclass-target (es. *Lanciare Incantesimi Arcani*
 *    via *Combattente Multiclasse* → +*Corpus Arcano* +*Mana*).
 *
 * **Why questa funzione esiste**: `getActiveSpellGrantsForLevelUpMany`
 * filtra le grant attive in base alle `justAcquired`. La regola Mago
 * «+1 spell ogni level-up» di `mago.corpus-arcano` (`trigger:
 * 'on-each-level-up'`) si attiva da subito al level-up corrente se
 * Corpus Arcano è appena acquisito via bundle multiclasse — quindi
 * dev'essere in `justAcquired`.
 */
export function buildJustAcquiredMoves(
  classId: Character['classId'],
  moveAdded: MoveRef | undefined,
  grantedChoice: LevelUpGrantedChoiceDraft | undefined,
  multiclassPick: LevelUpMulticlassPickDraft | undefined,
): readonly MoveRef[] {
  if (!moveAdded) return [];
  const result: MoveRef[] = [moveAdded];
  const mainMoveDef = findMoveByIdAnyScope(classId, moveAdded.id);
  if (grantedChoice && mainMoveDef?.grantsMoveChoice?.kind === 'from-group') {
    result.push({ id: grantedChoice.selectedOptionId, selectedChoices: [] });
  }
  if (multiclassPick) {
    result.push({
      id: multiclassPick.selectedMoveId,
      selectedChoices: [],
      ...(multiclassPick.selectedMoveSetup ?? {}),
    });
    // **FI-84 enhanced (2026-05-24)** — Cascade granted-chain: tutte le
    // mosse concesse via grantsMoveChoice in cascade vengono trattate
    // come justAcquired ai fini delle spell grants. Caso pilota:
    // Guerriero → Compagno d'Armi → Faro → Ordine Sacro (chain[0]) →
    // bundle [chierico.LI-divini, chierico.mana] → LI Divini ha
    // grantsSpells pick (3 spell L1 Chierico).
    appendGrantedChainMoves(result, multiclassPick);
  }
  appendBundleMoves(result, moveAdded.id, multiclassPick);
  return result;
}

function appendGrantedChainMoves(
  result: MoveRef[],
  multiclassPick: LevelUpMulticlassPickDraft,
): void {
  const chain = multiclassPick.grantedChain ?? [];
  for (const step of chain) {
    result.push({
      id: step.selectedMoveId,
      selectedChoices: [],
      ...(step.selectedSetup ?? {}),
    });
  }
}

function appendBundleMoves(
  result: MoveRef[],
  mainMoveId: string,
  multiclassPick: LevelUpMulticlassPickDraft | undefined,
): void {
  const anchors = collectBundleAnchors(mainMoveId, multiclassPick);
  for (const anchorId of anchors) {
    appendBundleMovesForAnchor(result, anchorId, multiclassPick);
  }
}

function collectBundleAnchors(
  mainMoveId: string,
  multiclassPick: LevelUpMulticlassPickDraft | undefined,
): readonly string[] {
  const anchors: string[] = [mainMoveId];
  if (!multiclassPick) return anchors;
  anchors.push(multiclassPick.selectedMoveId);
  // **FI-84 enhanced** — anche le mosse della grantedChain possono essere
  // anchor di bundle (es. chain[0] = Vincolo Tomo → bundle Mana + LI del
  // Patrono che ha grantsSpells pick).
  for (const step of multiclassPick.grantedChain ?? []) {
    anchors.push(step.selectedMoveId);
  }
  return anchors;
}

function appendBundleMovesForAnchor(
  result: MoveRef[],
  anchorId: string,
  multiclassPick: LevelUpMulticlassPickDraft | undefined,
): void {
  const anchor = findMoveByIdGlobal(anchorId);
  if (!anchor?.bundles) return;
  for (const bundleId of anchor.bundles) {
    const setup =
      anchorId === multiclassPick?.selectedMoveId
        ? multiclassPick.bundleSetups?.[bundleId]
        : undefined;
    result.push({ id: bundleId, selectedChoices: [], ...(setup ?? {}) });
  }
}
